// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
/**
 * Avoirs : le seul document qui réduit une facture déjà émise. L'onglet a son
 * propre cycle de vie (brouillon, émis, déposé, soldé) mais partage avec les
 * factures `utils/formatFacturation` et `./common`, pour qu'une facture et son
 * avoir s'affichent au même format.
 *
 * Aucun avoir n'est proposé sur une facture qui ne peut pas en recevoir : la
 * raison s'affiche à la place du bouton. Un avoir intégral recopie côté serveur
 * les lignes de la facture, sans écart de centimes. Le sort de l'avoir
 * (imputation ou remboursement) se lit dans l'état réel de la facture servi par
 * l'API. Un avoir émis ne s'annule pas : il a éteint une dette par un document
 * opposable, et la correction d'un avoir de trop est une facture.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, Grid,
  IconButton, InputLabel, MenuItem, Paper, Radio, RadioGroup, Select, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../utils/api';
import {
  NOT_PROVIDED, toCents, dateFr, euros, timestampFr, billingErrorMessage, percentBp,
  quantityFr, downloadDocument,
} from '../../utils/formatFacturation';
import { Champ, CHANNEL_LABEL } from './commun';

const CREDIT_NOTE_STATUSES = {
  draft: 'Brouillon',
  issued: 'Émis',
  deposited: 'Déposé',
  settled: 'Soldé',
};

const CREDIT_NOTE_STATUS_COLOR = {
  draft: 'default',
  issued: 'info',
  deposited: 'primary',
  settled: 'success',
};

const SORTS = {
  imputation: 'Imputation sur la facture',
  remboursement: 'Remboursement au client',
};

// États d'une facture qui acceptent un avoir, miroir de
// `credit_notes.obstacles_a_l_avoir` côté serveur : un brouillon n'existe dans
// aucune comptabilité, une facture annulée ne doit plus rien.
const FACTURE_CREDITABLE = ['issued', 'deposited', 'available', 'rejected', 'paid'];

/**
 * Ce qui empêche d'ouvrir un avoir sur cette facture, ou null.
 * Évite de proposer un bouton qui rendrait 409, avec la formulation du serveur
 * pour qu'un même refus ne reçoive pas deux explications.
 */
export function creditNoteObstacle(f) {
  if (!f) return 'Aucune facture sélectionnée.';
  if (f.status === 'draft' || !f.number) {
    return 'Cette facture n\'est pas émise : elle ne porte aucun numéro, elle n\'existe dans aucune comptabilité, il n\'y a rien à créditer. La corriger, ou la supprimer.';
  }
  if (f.status === 'cancelled') {
    return `La facture ${f.number} est annulée : plus rien n'est dû, un avoir créditerait une créance déjà éteinte.`;
  }
  if (!FACTURE_CREDITABLE.includes(f.status)) return `Statut « ${f.status} » : avoir impossible.`;
  const restant = (f.total_ttc_cents || 0) - (f.credited_amount_cents || 0);
  if (restant <= 0) {
    return `La facture ${f.number} est déjà intégralement créditée (${euros(f.credited_amount_cents)} d'avoirs pour ${euros(f.total_ttc_cents)} facturés).`;
  }
  return null;
}

/**
 * Taux de TVA imposés à un avoir partiel en franchise en base, ou null si
 * l'émetteur est assujetti (taux libre). En franchise, aucun taux ne se saisit :
 * [0] pour une facture sans TVA ; pour une facture émise avec TVA, ses taux
 * triés, car l'avoir rend la taxe réellement payée. Un régime non servi vaut
 * franchise, comme dans `DraftDialog` et `DialogueFacture`.
 */
export function fixedCreditNoteRates(facture, regime) {
  if (regime && !regime.franchise) return null;
  const rate = new Set();
  (facture?.lines || []).forEach((l) => {
    if (l?.vat_rate_bp === null || l?.vat_rate_bp === undefined) return;
    const n = Number(l.vat_rate_bp);
    if (Number.isFinite(n)) rate.add(n);
  });
  Object.entries(facture?.vat_breakdown || {}).forEach(([bp, montant]) => {
    const n = Number(bp);
    if (Number(montant) && Number.isFinite(n)) rate.add(n);
  });
  if (![...rate].some((t) => t > 0)) return [0];
  return [...rate].sort((x, y) => x - y);
}

function LinesTable({ lignes: rows }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Désignation</TableCell>
          <TableCell align="right">Quantité</TableCell>
          <TableCell>Unité</TableCell>
          <TableCell align="right">Prix unitaire HT</TableCell>
          <TableCell align="right">Total HT</TableCell>
          <TableCell align="right">TVA</TableCell>
          <TableCell align="right">Total TTC</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(rows || []).map((l) => (
          <TableRow key={l.id}>
            <TableCell>{l.description}</TableCell>
            <TableCell align="right">{quantityFr(l.quantity)}</TableCell>
            <TableCell>{l.unit || ''}</TableCell>
            <TableCell align="right">{euros(l.unit_price_ht_cents)}</TableCell>
            <TableCell align="right">{euros(l.line_ht_cents)}</TableCell>
            <TableCell align="right">{percentBp(l.vat_rate_bp)}</TableCell>
            <TableCell align="right">{euros(l.line_ttc_cents)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const EMPTY_ROW = { description: '', quantite: '1', unite: 'forfait', prix: '', tva: '' };

/**
 * Formulaire d'avoir. En mode intégral (par défaut, cas le plus fréquent), le
 * serveur recopie les lignes de la facture, seule garantie d'égalité au
 * centime. En mode partiel, les lignes sont saisies ; un taux de TVA vide prend
 * celui de la facture si elle n'en porte qu'un, sinon l'API refuse. En
 * franchise en base, le taux est imposé (voir `fixedCreditNoteRates`).
 */
function DialogueCreation({ facture, motifs: reasons, regime, onFermer: onClose, onCree }) {
  const [motif, setMotif] = useState('');
  const [text, setText] = useState('');
  const [mode, setMode] = useState('integral');
  const [rows, setLines] = useState([{ ...EMPTY_ROW }]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [cap, setCap] = useState(null);
  const [sending, setSending] = useState(false);

  const restant = (facture?.total_ttc_cents || 0) - (facture?.credited_amount_cents || 0);
  const fixedRates = fixedCreditNoteRates(facture, regime);

  // Le taux envoyé pour une ligne. Assujetti : ce qui est saisi, vide = null
  // (le serveur reprend le taux de la facture). Franchise : le taux imposé, ou
  // celui choisi parmi les taux de la facture quand elle en porte plusieurs.
  const lineRate = (l) => {
    if (!fixedRates) return l.tva === '' ? null : Number(l.tva);
    if (fixedRates.length === 1) return fixedRates[0];
    const n = l.tva === '' ? NaN : Number(l.tva);
    return fixedRates.includes(n) ? n : null;
  };

  const updateRow = (i, champ, valeur) => setLines((ls) => ls.map(
    (l, k) => (k === i ? { ...l, [champ]: valeur } : l),
  ));

  const enteredTotal = mode === 'integral'
    ? facture?.total_ttc_cents || 0
    : rows.reduce((sum, l) => {
      const pu = toCents(l.prix);
      const q = Number(String(l.quantite).replace(',', '.'));
      if (pu === null || Number.isNaN(q)) return sum;
      // Approximation d'affichage seulement : le calcul qui fait foi est celui
      // du serveur, en centimes entiers, avec un seul arrondi par ligne.
      const ht = Math.round(pu * q);
      const bp = lineRate(l);
      return sum + ht + (bp === null ? 0 : Math.round((ht * bp) / 10000));
    }, 0);

  const submit = async () => {
    setSending(true); setError(''); setCap(null);
    try {
      const body = {
        invoice_id: facture.id,
        reason_code: motif,
        reason: text.trim(),
        notes: notes.trim() || null,
      };
      if (mode === 'partiel') {
        body.lignes = rows
          .filter((l) => l.description.trim() && toCents(l.prix) !== null)
          .map((l) => ({
            description: l.description.trim(),
            unit_price_ht_cents: toCents(l.prix),
            quantity_hundredths: Math.round(Number(String(l.quantite).replace(',', '.')) * 100),
            unit: l.unite || null,
            vat_rate_bp: lineRate(l),
          }));
      }
      const r = await api.post('/avoirs', body);
      onCree(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.creditable_restant_cents !== undefined) {
        setCap(d);
      }
      setError(billingErrorMessage(e, 'Avoir non créé'));
    } finally {
      setSending(false);
    }
  };

  const validRows = mode === 'integral' || (rows.some(
    (l) => l.description.trim() && toCents(l.prix) !== null,
  ) && (!fixedRates || fixedRates.length === 1 || rows
    .filter((l) => l.description.trim() && toCents(l.prix) !== null)
    .every((l) => lineRate(l) !== null)));
  const readyToSend = Boolean(motif && text.trim().length >= 3 && validRows);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Avoir sur la facture {facture?.number}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Ce que cet avoir va faire</AlertTitle>
          Dès son émission, il éteint une part de la créance : le reste dû de la
          facture baisse d&apos;autant, elle sort des impayés et cesse de produire des
          intérêts sur la part rendue. Un avoir émis ne s&apos;annule pas.
        </Alert>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Champ label="Facturé" xs={3}>{euros(facture?.total_ttc_cents)}</Champ>
          <Champ label="Déjà crédité" xs={3}>{euros(facture?.credited_amount_cents)}</Champ>
          <Champ label="Créditable au maximum" xs={3}>
            <Typography variant="body2" fontWeight="medium">{euros(restant)}</Typography>
          </Champ>
          <Champ label="Destinataire" xs={3}>{facture?.buyer_legal_name || NOT_PROVIDED}</Champ>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={5}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Motif</InputLabel>
              <Select value={motif} label="Motif" onChange={(e) => setMotif(e.target.value)}>
                {reasons.map((m) => <MenuItem key={m.code} value={m.code}>{m.libelle}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={7}>
            <TextField
              fullWidth size="small" required label="Motif en clair, tel qu'il s'imprimera"
              value={text} onChange={(e) => setText(e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText="Mention obligatoire (art. 242 nonies A de l'annexe II au CGI) : il figure sur le document remis au client."
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Montant crédité</Typography>
          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value="integral" control={<Radio size="small" />}
              label={`Avoir intégral (${euros(facture?.total_ttc_cents)})`} />
            <FormControlLabel value="partiel" control={<Radio size="small" />}
              label="Avoir partiel, lignes saisies" />
          </RadioGroup>
          {mode === 'integral' ? (
            <Typography variant="caption" color="text.secondary">
              Les lignes de la facture sont recopiées telles quelles par le serveur, taux
              de TVA compris. Aucune ressaisie : c&apos;est ce qui garantit que l&apos;avoir
              vaut la facture au centime près.
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Montants POSITIFS : c&apos;est la nature du document qui porte le signe.
              {!fixedRates && " Taux de TVA laissé vide = celui de la facture, si elle n'en porte qu'un."}
              {fixedRates && fixedRates.length === 1 && fixedRates[0] === 0
                && " Franchise en base (art. 293 B du CGI) : l'avoir ne porte aucune TVA."}
              {fixedRates && fixedRates.length === 1 && fixedRates[0] > 0
                && ` Franchise en base : cette facture a été émise avec une TVA de ${percentBp(fixedRates[0])}, l'avoir reprend ce taux pour rendre la taxe payée.`}
              {fixedRates && fixedRates.length > 1
                && " Franchise en base : cette facture porte plusieurs taux, chaque ligne reprend l'un d'eux."}
            </Typography>
          )}
        </Box>

        {mode === 'partiel' && (
          <Box sx={{ mt: 1 }}>
            {rows.map((l, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="Désignation" value={l.description}
                    onChange={(e) => updateRow(i, 'description', e.target.value)}
                    inputProps={{ maxLength: 300 }} />
                </Grid>
                <Grid item xs={2}>
                  <TextField fullWidth size="small" label="Quantité" value={l.quantite}
                    onChange={(e) => updateRow(i, 'quantite', e.target.value)} />
                </Grid>
                <Grid item xs={2}>
                  <TextField fullWidth size="small" label="Unité" value={l.unite}
                    onChange={(e) => updateRow(i, 'unite', e.target.value)}
                    inputProps={{ maxLength: 20 }} />
                </Grid>
                <Grid item xs={2}>
                  <TextField fullWidth size="small" label="Prix unitaire HT (€)" value={l.prix}
                    onChange={(e) => updateRow(i, 'prix', e.target.value)} />
                </Grid>
                <Grid item xs={1}>
                  {!fixedRates && (
                    <TextField fullWidth size="small" label="TVA (bp)" value={l.tva}
                      onChange={(e) => updateRow(i, 'tva', e.target.value)} />
                  )}
                  {fixedRates && fixedRates.length === 1 && (
                    <TextField fullWidth size="small" label="TVA (bp)" value={String(fixedRates[0])}
                      disabled inputProps={{ readOnly: true }} />
                  )}
                  {fixedRates && fixedRates.length > 1 && (
                    <TextField fullWidth size="small" select label="TVA (bp)" value={l.tva}
                      onChange={(e) => updateRow(i, 'tva', e.target.value)}>
                      {fixedRates.map((t) => (
                        <MenuItem key={t} value={String(t)}>{t}</MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item xs={1}>
                  <IconButton size="small" disabled={rows.length === 1}
                    onClick={() => setLines((ls) => ls.filter((_, k) => k !== i))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => setLines((ls) => [...ls, { ...EMPTY_ROW }])}>
              Ajouter une ligne
            </Button>
          </Box>
        )}

        <TextField
          fullWidth size="small" multiline minRows={2} sx={{ mt: 2 }}
          label="Observations (facultatif)" value={notes}
          onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 4000 }}
        />

        {enteredTotal > restant && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Le montant saisi ({euros(enteredTotal)}) dépasse ce qui peut encore être
            crédité ({euros(restant)}). L&apos;API refusera : on ne rend pas plus que ce
            qui a été facturé.
          </Alert>
        )}
        {cap && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Plafond dépassé</AlertTitle>
            {euros(cap.facture_cents)} facturés, {euros(cap.deja_credite_cents)} déjà
            crédités : il reste {euros(cap.creditable_restant_cents)} créditables.
          </Alert>
        )}
        {error && !cap && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={!readyToSend || sending} onClick={submit}>
          {sending ? <CircularProgress size={18} /> : 'Créer le brouillon'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Dépôt, remise par courriel, solde : trois formulaires courts, un dialogue. */
function DialogueAction({ action, onFermer: onClose, onFait: onDone }) {
  const [fields, setFields] = useState({});
  const [error, setError] = useState('');
  const [steps, setSteps] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => { setFields({}); setError(''); setSteps(null); }, [action?.type, action?.avoir?.id]);

  if (!action) return null;
  const { type, avoir: creditNote } = action;
  const refundDue = creditNote?.facture?.remboursement_du_cents || 0;

  const submit = async () => {
    setSending(true); setError(''); setSteps(null);
    try {
      let r;
      if (type === 'deposer') {
        r = await api.post(`/avoirs/${creditNote.id}/deposer`, {
          external_ref: fields.external_ref || null, canal: fields.canal || null,
        });
      } else if (type === 'envoyer') {
        r = await api.post(`/avoirs/${creditNote.id}/envoyer`, {
          destinataire: fields.destinataire || null,
        });
      } else {
        r = await api.post(`/avoirs/${creditNote.id}/solder`, {
          mode: fields.mode || (refundDue > 0 ? 'remboursement' : 'imputation'),
          montant_cents: fields.montant ? toCents(fields.montant) : null,
          reference: fields.reference || null,
        });
      }
      onDone(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 501 && d?.etapes) setSteps(d);
      setError(billingErrorMessage(e, 'Action refusée'));
    } finally {
      setSending(false);
    }
  };

  const titles = {
    deposer: 'Enregistrer un dépôt',
    envoyer: 'Remettre l\'avoir par courriel',
    solder: 'Solder l\'avoir',
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{titles[type]} — {creditNote?.number}</DialogTitle>
      <DialogContent dividers>
        {type === 'deposer' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Un avoir se dépose par le même chemin que la facture qu&apos;il corrige.
              Pour un acheteur public, sans dépôt sur le portail, le comptable ne le
              reçoit jamais et continue de devoir la totalité.
            </Alert>
            <TextField label="Canal" select size="small" value={fields.canal || creditNote?.channel || 'manuel'}
              onChange={(e) => setFields((c) => ({ ...c, canal: e.target.value }))}>
              {Object.entries(CHANNEL_LABEL).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
            <TextField label="Référence de dépôt" size="small" required
              value={fields.external_ref || ''} inputProps={{ maxLength: 80 }}
              onChange={(e) => setFields((c) => ({ ...c, external_ref: e.target.value }))}
              helperText="Le numéro rendu par le portail ou le destinataire. Sans elle, le dépôt n'est pas prouvable et l'API refuse." />
          </Stack>
        )}

        {type === 'envoyer' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Le courriel dit ce que l&apos;avoir change pour le destinataire, dans les
              mêmes termes que le document joint. Aucune coordonnée bancaire n&apos;y figure.
            </Alert>
            <TextField label="Adresse (facultatif)" size="small" value={fields.destinataire || ''}
              inputProps={{ maxLength: 255 }}
              onChange={(e) => setFields((c) => ({ ...c, destinataire: e.target.value }))}
              helperText="Vide = le contact de facturation de l'établissement. La comptabilité n'est presque jamais l'adresse par laquelle le contrat s'est signé." />
          </Stack>
        )}

        {type === 'solder' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {refundDue > 0 ? (
              <Alert severity="warning">
                <AlertTitle>Un remboursement est dû</AlertTitle>
                La facture a été réglée : il reste {euros(refundDue)} de trop-perçu à rendre
                au client. Une imputation ne lui rendrait rien, et l&apos;API la refuse.
              </Alert>
            ) : (
              <Alert severity="success">
                Rien à rembourser : la facture n&apos;a pas été payée au delà de ce qui
                restait dû. Cet avoir s&apos;impute, il éteint la créance à due concurrence.
              </Alert>
            )}
            <FormControl size="small">
              <InputLabel>Sort de l&apos;avoir</InputLabel>
              <Select label="Sort de l'avoir"
                value={fields.mode || (refundDue > 0 ? 'remboursement' : 'imputation')}
                onChange={(e) => setFields((c) => ({ ...c, mode: e.target.value }))}>
                {Object.entries(SORTS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </Select>
            </FormControl>
            {(fields.mode || (refundDue > 0 ? 'remboursement' : 'imputation')) === 'remboursement' && (
              <>
                <TextField label="Montant remboursé (€)" size="small"
                  value={fields.montant === undefined ? String((refundDue / 100).toFixed(2)) : fields.montant}
                  onChange={(e) => setFields((c) => ({ ...c, montant: e.target.value }))}
                  helperText={`Au maximum ${euros(refundDue)}. On ne rend pas plus que ce qui a été reçu en trop.`} />
                <TextField label="Référence du virement" size="small" value={fields.reference || ''}
                  inputProps={{ maxLength: 120 }}
                  onChange={(e) => setFields((c) => ({ ...c, reference: e.target.value }))} />
              </>
            )}
          </Stack>
        )}

        {steps && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Canal non branché</AlertTitle>
            {steps.message}
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
              {(steps.etapes || []).map((s) => <li key={s}><Typography variant="body2">{s}</Typography></li>)}
            </Box>
          </Alert>
        )}
        {error && !steps && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
        <Button variant="contained" disabled={sending || (type === 'deposer' && !fields.external_ref)}
          onClick={submit}>
          {sending ? <CircularProgress size={18} /> : 'Valider'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreditNoteDialog({ avoir: creditNote, onFermer: onClose, onRafraichir: onRefresh, onChange }) {
  const [docError, setDocError] = useState('');
  const [error, setError] = useState('');
  const [missing, setMissing] = useState(null);
  const [action, setAction] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => { setDocError(''); setError(''); setMissing(null); }, [creditNote?.id]);

  if (!creditNote) return null;
  const a = creditNote;
  const draft = a.status === 'draft';

  const download = async (format) => {
    const label = a.number || `brouillon-${a.id.slice(0, 8)}`;
    setDocError(await downloadDocument(api, `/avoirs/${a.id}`, label, format) || '');
  };

  const issue = async () => {
    setWorking(true); setError(''); setMissing(null);
    try {
      const r = await api.post(`/avoirs/${a.id}/emettre`);
      onChange(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (Array.isArray(d?.manquantes)) setMissing(d.manquantes);
      setError(billingErrorMessage(e, 'Émission refusée'));
    } finally {
      setWorking(false);
    }
  };

  const remove = async () => {
    setWorking(true); setError('');
    try {
      await api.delete(`/avoirs/${a.id}`);
      onChange(null);
    } catch (e) {
      setError(billingErrorMessage(e, 'Suppression refusée'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {a.number || 'Brouillon, non numéroté'}
          <Chip size="small" label={CREDIT_NOTE_STATUSES[a.status] || a.status}
            color={CREDIT_NOTE_STATUS_COLOR[a.status] || 'default'} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Recharger depuis l'API">
            <IconButton size="small" onClick={() => onRefresh(a.id)}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          {draft && (a.mentions_manquantes || []).length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Cet avoir ne peut pas encore être émis</AlertTitle>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                {a.mentions_manquantes.map((m) => <li key={m}><Typography variant="body2">{m}</Typography></li>)}
              </Box>
            </Alert>
          )}
          {a.effet && (
            <Alert severity={a.settlement_mode === 'remboursement' ? 'warning' : 'info'} sx={{ mb: 2 }}>
              {a.effet}
            </Alert>
          )}
          {missing && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Émission refusée</AlertTitle>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                {missing.map((m) => <li key={m}><Typography variant="body2">{m}</Typography></li>)}
              </Box>
            </Alert>
          )}
          {error && !missing && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {docError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDocError('')}>{docError}</Alert>}

          <Grid container spacing={2}>
            <Champ label="Facture corrigée" xs={4}>{a.invoice_number || NOT_PROVIDED}</Champ>
            <Champ label="Motif" xs={4}>{a.reason_libelle || a.reason_code}</Champ>
            <Champ label="Date d'émission" xs={4}>{dateFr(a.issue_date)}</Champ>
            <Champ label="Destinataire" xs={4}>{a.buyer_legal_name || NOT_PROVIDED}</Champ>
            <Champ label="SIRET destinataire" xs={4}>{a.buyer_siret || NOT_PROVIDED}</Champ>
            <Champ label="Canal de transmission" xs={4}>{CHANNEL_LABEL[a.channel] || a.channel}</Champ>
            <Champ label="Période concernée" xs={4}>
              {a.period_start ? `${dateFr(a.period_start)} au ${dateFr(a.period_end)}` : NOT_PROVIDED}
            </Champ>
            <Champ label="Déposé le" xs={4}>{timestampFr(a.deposited_at)}</Champ>
            <Champ label="Référence de dépôt" xs={4}>{a.external_ref || NOT_PROVIDED}</Champ>
            <Champ label="Motif en clair" xs={12}>{a.reason}</Champ>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Prestations créditées</Typography>
          <LinesTable lignes={a.lines} />

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Montant de l&apos;avoir</Typography>
                <Stack spacing={0.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total hors taxes</Typography>
                    <Typography variant="body2">{euros(a.total_ht_cents)}</Typography>
                  </Box>
                  {Object.keys(a.vat_breakdown || {}).sort((x, y) => Number(x) - Number(y)).map((bp) => (
                    <Box key={bp} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">TVA au taux de {percentBp(bp)}</Typography>
                      <Typography variant="body2" color="text.secondary">{euros(a.vat_breakdown[bp])}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" fontWeight="bold">Total TTC crédité</Typography>
                    <Typography variant="body2" fontWeight="bold">{euros(a.total_ttc_cents)}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Effet sur la facture</Typography>
                {a.facture ? (
                  <Stack spacing={0.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Facturé</Typography>
                      <Typography variant="body2">{euros(a.facture.total_ttc_cents)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Encaissé</Typography>
                      <Typography variant="body2">{euros(a.facture.paid_amount_cents)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Crédité par avoirs</Typography>
                      <Typography variant="body2">{euros(a.facture.credited_amount_cents)}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight="medium">Reste dû</Typography>
                      <Typography variant="body2" fontWeight="medium"
                        color={a.facture.outstanding_cents > 0 ? 'warning.main' : 'success.main'}>
                        {euros(a.facture.outstanding_cents)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">À rembourser</Typography>
                      <Typography variant="body2"
                        color={a.facture.remboursement_du_cents > 0 ? 'error.main' : 'text.secondary'}>
                        {euros(a.facture.remboursement_du_cents)}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Facture non chargée.
                  </Typography>
                )}
                {a.settlement_mode && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Soldé le {timestampFr(a.settled_at)} : {SORTS[a.settlement_mode]}
                    {a.refund_amount_cents ? `, ${euros(a.refund_amount_cents)}` : ''}
                    {a.refund_reference ? ` (${a.refund_reference})` : ''}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {a.notes && <Typography variant="body2" sx={{ mt: 2 }}>Observations : {a.notes}</Typography>}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Journal</Typography>
          {(a.events_log || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">Aucun évènement enregistré.</Typography>
          ) : (
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {[...(a.events_log || [])].reverse().map((ev, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Typography key={i} variant="body2" sx={{ fontSize: '0.8rem' }}>
                  {timestampFr(ev.at)} : {ev.kind}{ev.detail ? ` , ${ev.detail}` : ''}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button size="small" startIcon={<DescriptionIcon />} onClick={() => download('md')}>
            Document Markdown
          </Button>
          <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => download('pdf')}>
            Document PDF
          </Button>
          <Box sx={{ flex: 1 }} />
          {draft && (
            <>
              <Button size="small" color="error" disabled={working} onClick={remove}>
                Supprimer le brouillon
              </Button>
              <Button size="small" variant="contained" disabled={working} onClick={issue}>
                {working ? <CircularProgress size={16} /> : 'Émettre'}
              </Button>
            </>
          )}
          {a.status === 'issued' && (
            <Button size="small" onClick={() => setAction({ type: 'deposer', avoir: a })}>
              Enregistrer un dépôt
            </Button>
          )}
          {!draft && (
            <Button size="small" onClick={() => setAction({ type: 'envoyer', avoir: a })}>
              Envoyer par courriel
            </Button>
          )}
          {['issued', 'deposited'].includes(a.status) && (
            <Button size="small" variant="contained"
              onClick={() => setAction({ type: 'solder', avoir: a })}>
              Solder
            </Button>
          )}
          <Button onClick={onClose}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <DialogueAction
        action={action}
        onFermer={() => setAction(null)}
        onFait={(view) => {
          setAction(null);
          // La réponse de l'envoi enveloppe l'avoir ; celles du dépôt et du
          // solde sont l'avoir lui-même.
          onChange(view?.avoir || view);
        }}
      />
    </>
  );
}

/**
 * Avoirs d'une facture et bouton pour en créer un, affichés dans le détail de
 * la facture : un avoir naît toujours d'une facture qu'on regarde, jamais
 * d'une liste d'avoirs.
 */
export function CreditNotesBlock({ facture, regime, onChange }) {
  const [creditNotes, setCreditNotes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [creation, setCreation] = useState(false);
  const [isOpen, setIsOpen] = useState(null);
  const [error, setError] = useState('');

  const charger = useCallback(async () => {
    if (!facture?.id) return;
    setError('');
    try {
      const r = await api.get('/avoirs', { params: { invoice_id: facture.id } });
      setCreditNotes(r.data.avoirs || []);
    } catch (e) {
      setError(billingErrorMessage(e, 'Avoirs illisibles'));
    }
  }, [facture?.id]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    let alive = true;
    api.get('/avoirs/motifs')
      .then((r) => { if (alive) setReasons(r.data.motifs || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const openItem = useCallback(async (id) => {
    try {
      const r = await api.get(`/avoirs/${id}`);
      setIsOpen(r.data);
    } catch (e) {
      setError(billingErrorMessage(e, 'Avoir illisible'));
    }
  }, []);

  if (!facture) return null;
  const obstacle = creditNoteObstacle(facture);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="subtitle2">Avoirs</Typography>
        <Box sx={{ flex: 1 }} />
        {obstacle ? (
          <Tooltip title={obstacle}>
            <span><Button size="small" disabled>Créer un avoir</Button></span>
          </Tooltip>
        ) : (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreation(true)}>
            Créer un avoir
          </Button>
        )}
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}

      {creditNotes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Aucun avoir sur cette facture.{obstacle ? ` ${obstacle}` : ''}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Numéro</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Motif</TableCell>
              <TableCell align="right">Montant TTC</TableCell>
              <TableCell>Sort</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {creditNotes.map((a) => (
              <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => openItem(a.id)}>
                <TableCell>{a.number || 'brouillon'}</TableCell>
                <TableCell>
                  <Chip size="small" label={CREDIT_NOTE_STATUSES[a.status] || a.status}
                    color={CREDIT_NOTE_STATUS_COLOR[a.status] || 'default'} />
                </TableCell>
                <TableCell>{a.reason_libelle || a.reason_code}</TableCell>
                <TableCell align="right">{euros(a.total_ttc_cents)}</TableCell>
                <TableCell>{a.settlement_mode ? SORTS[a.settlement_mode] : '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={(e) => { e.stopPropagation(); openItem(a.id); }}>
                    Ouvrir
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {creation && (
        <DialogueCreation
          facture={facture}
          motifs={reasons}
          regime={regime}
          onFermer={() => setCreation(false)}
          onCree={(creditNote) => {
            setCreation(false);
            setIsOpen(creditNote);
            charger();
            if (onChange) onChange();
          }}
        />
      )}

      {isOpen && (
        <CreditNoteDialog
          avoir={isOpen}
          onFermer={() => setIsOpen(null)}
          onRafraichir={openItem}
          onChange={(view) => {
            setIsOpen(view);
            charger();
            // La facture a changé de reste dû : l'écran qui l'affiche doit le
            // relire, sinon il montre encore la créance d'avant l'avoir.
            if (onChange) onChange();
          }}
        />
      )}
    </Box>
  );
}

/**
 * Droits à avoir signalés au titre de l'article 7.2 d) et non traités, tous
 * établissements confondus : obligation de facturation, ils s'affichent avec
 * les avoirs. Rien n'est émis automatiquement. L'avoir se crée à la main depuis
 * la facture du mois concerné, puis le droit se déclare traité ; dans l'ordre
 * inverse, le droit quitterait la liste avant que l'avoir n'existe.
 */
function CreditNoteEntitlements() {
  const [entitlements, setEntitlements] = useState([]);
  const [note, setNote] = useState('');
  const [state, setState] = useState({ charge: true, erreur: '', refuse: false });
  const [processing, setProcessing] = useState(null); // { droit, note, enCours, erreur }

  const charger = useCallback(async () => {
    setState((e) => ({ ...e, charge: true, erreur: '' }));
    try {
      const r = await api.get('/pro/admin/quality/credit-entitlements');
      setEntitlements(r.data.entitlements || []);
      setNote(r.data.note || '');
      setState({ charge: false, erreur: '', refuse: false });
    } catch (e) {
      const refuse = e?.response?.status === 403;
      setState({ charge: false, refuse, erreur: refuse ? '' : billingErrorMessage(e, 'Droits à avoir illisibles') });
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const declarer = async () => {
    const t = processing;
    if (!t || !t.note.trim()) return;
    setProcessing({ ...t, enCours: true, erreur: '' });
    try {
      await api.post(`/pro/admin/false-alert-breaches/${t.droit.id}/handled`, {
        note: t.note.trim(),
      });
      setProcessing(null);
      charger();
    } catch (e) {
      setProcessing({ ...t, enCours: false, erreur: billingErrorMessage(e, 'Refusé par le serveur') });
    }
  };

  if (state.charge && !entitlements.length) return null;
  if (state.refuse) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Droits à avoir de l&apos;article 7.2 d) illisibles avec ce compte (permission
        facturation ou gestion de l&apos;équipe requise).
      </Alert>
    );
  }
  if (state.erreur) return <Alert severity="error" sx={{ mb: 2 }}>{state.erreur}</Alert>;
  if (!entitlements.length) return null;

  return (
    <Paper sx={{ p: 2, mb: 2, borderLeft: 4, borderColor: 'error.main' }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {entitlements.length} droit(s) à avoir signalé(s), non traité(s) (art. 7.2 d)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {note} Émettez l&apos;avoir depuis la facture concernée, puis déclarez le droit traité.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Établissement</TableCell>
            <TableCell>Motif</TableCell>
            <TableCell>Signalé le</TableCell>
            <TableCell>Manquement</TableCell>
            <TableCell>Dû</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {entitlements.map((d) => (
            <TableRow key={d.id}>
              <TableCell>{d.site_name}</TableCell>
              <TableCell>{d.entitlement?.label || d.entitlement?.reason}</TableCell>
              <TableCell>{dateFr(d.entitlement?.at)}</TableCell>
              <TableCell>{(d.months || []).join(' et ')}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{d.entitlement?.what}</TableCell>
              <TableCell align="right">
                <Button size="small" variant="outlined"
                  onClick={() => setProcessing({ droit: d, note: '', enCours: false, erreur: '' })}>
                  Avoir émis
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!processing} onClose={() => !processing?.enCours && setProcessing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Déclarer traité : {processing?.droit?.site_name}</DialogTitle>
        <DialogContent dividers>
          {processing?.erreur && <Alert severity="error" sx={{ mb: 2 }}>{processing.erreur}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>
            À faire APRÈS avoir émis l&apos;avoir : le droit sort de cette liste et ne s&apos;y
            affiche plus.
          </Alert>
          {/* Obligatoire : un droit déclaré traité sort de la liste, et la
              note est la seule trace qui relie ce geste à l'avoir émis. */}
          <TextField fullWidth size="small" required label="Note (numéro de l'avoir émis)"
            helperText="Obligatoire : le numéro de l'avoir, pour retrouver ce qui a soldé le droit."
            value={processing?.note || ''}
            onChange={(e) => setProcessing((t) => ({ ...t, note: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessing(null)} disabled={processing?.enCours}>Annuler</Button>
          <Button variant="contained" onClick={declarer}
            disabled={processing?.enCours || !(processing?.note || '').trim()}>
            {processing?.enCours ? <CircularProgress size={18} /> : 'Déclarer traité'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default function CreditNotesTab({ onOuvrirFacture: onOpenInvoice }) {
  const [creditNotes, setCreditNotes] = useState([]);
  const [statusValue, setStatusValue] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(null);
  const [total, setTotal] = useState(0);

  const charger = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/avoirs', { params: statusValue ? { statut: statusValue } : {} });
      setCreditNotes(r.data.avoirs || []);
      setTotal(r.data.total_ttc_cents || 0);
    } catch (e) {
      setError(billingErrorMessage(e, 'Avoirs illisibles'));
    } finally {
      setLoading(false);
    }
  }, [statusValue]);

  useEffect(() => { charger(); }, [charger]);

  const openItem = useCallback(async (id) => {
    try {
      const r = await api.get(`/avoirs/${id}`);
      setIsOpen(r.data);
    } catch (e) {
      setError(billingErrorMessage(e, 'Avoir illisible'));
    }
  }, []);

  const aSolder = creditNotes.filter((a) => ['issued', 'deposited'].includes(a.status));

  return (
    <Box>
      <CreditNoteEntitlements />
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Un avoir se crée depuis la facture qu&apos;il corrige</AlertTitle>
        Ouvrir la facture dans l&apos;onglet « Factures », puis « Créer un avoir » : c&apos;est
        elle qui porte le destinataire, le canal et le plafond de ce qui peut être
        crédité. Cette liste sert à suivre ce qui a été rendu, et ce qui reste à solder.
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Statut</InputLabel>
          <Select value={statusValue} label="Statut" onChange={(e) => setStatusValue(e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            {Object.entries(CREDIT_NOTE_STATUSES).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </Select>
        </FormControl>
        <Button size="small" startIcon={<RefreshIcon />} onClick={charger}>Recharger</Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {creditNotes.length} avoir(s), {euros(total)} crédités
          {aSolder.length > 0 ? ` , dont ${aSolder.length} à solder` : ''}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Numéro</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Facture</TableCell>
              <TableCell>Destinataire</TableCell>
              <TableCell>Motif</TableCell>
              <TableCell align="right">Montant TTC</TableCell>
              <TableCell>Sort</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell>
              </TableRow>
            ) : creditNotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    Aucun avoir. C&apos;est la situation normale : un avoir corrige une
                    facture, il ne s&apos;en émet pas dans le cours ordinaire.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : creditNotes.map((a) => (
              <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => openItem(a.id)}>
                <TableCell>{a.number || 'brouillon'}</TableCell>
                <TableCell>
                  <Chip size="small" label={CREDIT_NOTE_STATUSES[a.status] || a.status}
                    color={CREDIT_NOTE_STATUS_COLOR[a.status] || 'default'} />
                </TableCell>
                <TableCell>
                  {a.invoice_number && onOpenInvoice ? (
                    <Button size="small" onClick={(e) => { e.stopPropagation(); onOpenInvoice(a.invoice_id); }}>
                      {a.invoice_number}
                    </Button>
                  ) : (a.invoice_number || NOT_PROVIDED)}
                </TableCell>
                <TableCell>{a.buyer_legal_name || NOT_PROVIDED}</TableCell>
                <TableCell>{a.reason_libelle || a.reason_code}</TableCell>
                <TableCell align="right">{euros(a.total_ttc_cents)}</TableCell>
                <TableCell>{a.settlement_mode ? SORTS[a.settlement_mode] : '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={(e) => { e.stopPropagation(); openItem(a.id); }}>Ouvrir</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {isOpen && (
        <CreditNoteDialog
          avoir={isOpen}
          onFermer={() => setIsOpen(null)}
          onRafraichir={openItem}
          onChange={(view) => { setIsOpen(view); charger(); }}
        />
      )}
    </Box>
  );
}
